/**
 * C-11 SlipTape · C-12 StampBadge · C-13 DuplicateNotice · C-14 AllocationForm · C-15 CaptureSuccess — mockup หน้าจอ 3–6
 *
 * v1 (SOT §15 v0.4.0): ไม่มีขั้นตรวจกับธนาคาร → หน้าจอพนักงานได้สถานะแค่ `pending`/`image` (ป้ายเหลือง "อ่านจากภาพ · รอตรวจ")
 * ป้ายเขียว "ธนาคารยืนยันแล้ว" (mockup 3) มีใน StampBadge ตาม COMPONENTS แต่**ไม่มีเส้นทางไหนใน flow ส่ง `bank`/`verified` เข้ามา** —
 * `tapeStatus()` คือจุดเดียวที่แปลงผลจากท่อเป็นสถานะ และคืนได้แค่ pending/image (เทสต์ f15-no-bank.test.ts)
 *
 * กฎเหล็กข้อ 7: ไม่มีคอมโพเนนต์ในไฟล์นี้รับ props ยอดรวม/โควตา/รายการของคนอื่น — รับได้แค่ข้อมูลของใบเดียวที่พนักงานคนนี้เพิ่งถ่าย
 */
import { bankShortName } from "@pidyod/slip-qr";
import { html, type Raw } from "../h.js";
import { Button, Money } from "./basics.js";

/* ---------------- C-12 StampBadge ---------------- */
export type StampKind = "verified" | "pending" | "duplicate" | "rejected";
const STAMP: Record<StampKind, { text: string; tone: "seal" | "amber" | "stamp" }> = {
  verified: { text: "ธนาคารยืนยันแล้ว", tone: "seal" },
  pending: { text: "รอตรวจ", tone: "amber" },
  duplicate: { text: "สลิปซ้ำ", tone: "stamp" },
  rejected: { text: "ปฏิเสธ", tone: "stamp" },
};
export function StampBadge(p: { kind: StampKind }): Raw {
  const s = STAMP[p.kind];
  return html`<span class="stamp stamp-${s.tone}" aria-hidden="true">${s.text}</span>`;
}

/* ---------------- C-11 SlipTape ---------------- */
/** ชั้นข้อมูลที่หน้าจอพนักงานมีตอนถ่าย: ชั้น A (QR) เท่านั้น · ชั้น C (AI) มาทีหลังจาก worker · ชั้น B ไม่มีใน v1 */
export interface TapeData {
  /** รหัสธนาคาร ธปท. จาก QR · null = QR อ่านไม่ได้ */
  qrBankCode: string | null;
  /** เลขอ้างอิงจาก QR ดิบ — แสดงบนจอได้ (ห้าม normalize · ห้ามพิมพ์ลง stdout) */
  transRef: string | null;
  capturedAt: Date;
  /** ยอดจากภาพ (ชั้น C) — null = ยังไม่ได้อ่าน (ปกติของ v1 ตอนถ่าย) */
  aiAmount: number | null;
}
export type TapeStatus = "pending" | "image";
export const TAPE_BADGE: Record<TapeStatus, string> = {
  pending: "◷ อ่านจากภาพ · รอตรวจ",
  image: "◷ อ่านจากภาพ · รอตรวจ",
};

/** จุดเดียวที่ตัดสินสถานะบนหน้าจอพนักงานจากผลของท่อ — v1 คืนได้แค่สองค่านี้ ไม่มี bank */
export function tapeStatus(r: { verify_status?: string; aiAmount?: number | null }): TapeStatus {
  return r.aiAmount !== null && r.aiAmount !== undefined ? "image" : "pending";
}

export function fmtDateTime(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function SlipTape(p: { data: TapeData; status: TapeStatus; warnings?: string[]; animate?: boolean }): Raw {
  const d = p.data;
  const rows: Array<[string, string, string]> = [];
  rows.push(["จาก", d.qrBankCode ? bankShortName(d.qrBankCode) : "— (รออ่านจากภาพ)", ""]);
  rows.push(["เวลาถ่าย", fmtDateTime(d.capturedAt), ""]);
  rows.push(["เลขอ้างอิง", d.transRef ?? "— (รออ่านจากภาพ)", d.transRef ? "" : "low"]);
  const warnings = p.warnings ?? [];
  return html`<div class="tape${p.animate === false ? " static" : ""}">
    <span class="tag tag-amber" data-status="${p.status}">${TAPE_BADGE[p.status]}</span>
    ${d.aiAmount !== null ? Money({ amount: d.aiAmount, size: "lg" }) : html`<div class="money-unknown">ยอด: รออ่านจากภาพ</div>`}
    <div class="tape-meta">${rows.map(([k, v, cls], i) => html`<div style="animation-delay:${(i * 0.05).toFixed(2)}s"><span class="k">${k}</span><span class="v${cls ? " " + cls : ""}">${v}</span></div>`)}</div>
    ${warnings.length ? html`<div class="tape-warn">${warnings.map((w, i) => html`${i ? html`<br>` : null}${w}`)}</div>` : null}
    ${StampBadge({ kind: "pending" })}
    ${tapeEdge()}
  </div>`;
}

/** ขอบล่างหยักฟันเลื่อย (design-system §5.1) — path สร้างด้วย JS · viewBox 0–100 ยืดเต็มความกว้าง */
export function tapeEdge(teeth = 24): Raw {
  const step = 100 / teeth;
  let d = "M0 0";
  for (let i = 0; i < teeth; i++) d += ` L${((i + 0.5) * step).toFixed(2)} 8 L${((i + 1) * step).toFixed(2)} 0`;
  return html`<svg class="tape-edge" viewBox="0 0 100 8" preserveAspectRatio="none" aria-hidden="true"><path d="${d} Z" fill="currentColor"/></svg>`;
}

/* ---------------- C-14 AllocationForm ---------------- */
export const KINDS: ReadonlyArray<{ id: "sale" | "service" | "deposit"; label: string }> = [
  { id: "sale", label: "ขายสินค้า" },
  { id: "service", label: "ค่าบริการ" },
  { id: "deposit", label: "มัดจำ" },
];
export type KindId = (typeof KINDS)[number]["id"];
export function AllocationForm(p: { kind: KindId; duplicate?: boolean; sending?: boolean; ctaLabel: string; ctaVariant?: "seal" | "amber" }): Raw {
  return html`<div class="pick">
    <label>ประเภท</label>
    <div class="seg" role="group" aria-label="ประเภท">${KINDS.map((k) => html`<button type="button" data-action="kind" data-kind="${k.id}" aria-pressed="${String(k.id === p.kind)}">${k.label}</button>`)}</div>
    ${Button({ label: p.ctaLabel, variant: p.ctaVariant ?? "amber", action: "save", loading: p.sending === true, disabled: p.duplicate === true })}
  </div>`;
}

/* ---------------- C-15 CaptureSuccess ---------------- */
/** หันจอให้ลูกค้า — มีแค่ ยอด(ถ้ารู้) · เวลา · ร้าน · ชื่อผู้รับ · ห้ามมีข้อมูลอื่นของร้าน */
export function CaptureSuccess(p: { amount: number | null; time: Date; shopName: string; staffName: string }): Raw {
  const d = p.time;
  const th = new Intl.DateTimeFormat("th-TH-u-ca-buddhist", { day: "numeric", month: "short", year: "numeric" }).format(d);
  const hm = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  return html`<div class="done" data-screen="success">
      <div class="tick" aria-hidden="true">✓</div>
      <h3>รับสลิปเรียบร้อย</h3>
      ${p.amount !== null ? Money({ amount: p.amount, size: "hero", currency: "" }) : html`<div class="amt-unknown">ยอดกำลังอ่านจากภาพ</div>`}
      <div class="sub">${th} · ${hm} น.</div>
      <div class="sub" style="margin-top:3px">${p.shopName} · รับโดย ${p.staffName}</div>
      <div class="show">ให้ลูกค้าดูหน้าจอนี้ได้เลย</div>
    </div>
    <button type="button" class="next" data-action="next">รับใบถัดไป</button>`;
}

/* ---------------- C-13 DuplicateNotice ---------------- */
/** v1: ใบเดิมรู้แค่เวลา (และร้าน ถ้าเป็นร้านเดียวกัน) — ยอดยังไม่มี · ถ้อยคำห้ามกล่าวหาพนักงาน */
export function DuplicateNotice(p: { firstSeen: { at: Date; shopName: string | null } | null }): Raw {
  return html`<div class="reject" data-screen="duplicate">
      <div class="mark" aria-hidden="true">✕</div>
      <h3>สลิปนี้ใช้ไปแล้ว</h3>
      <p>เลขอ้างอิงนี้เคยถูกบันทึกแล้ว<br>กรุณาขอสลิปใบใหม่จากลูกค้า</p>
      ${p.firstSeen ? html`<div class="box">บันทึกครั้งแรก<br>${fmtDateTime(p.firstSeen.at)} น.${p.firstSeen.shopName ? html`<br>${p.firstSeen.shopName}` : null}</div>` : null}
    </div>
    <button type="button" class="next red" data-action="next">ลองใบอื่น</button>`;
}
